import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '@ui-kit';
import { codeGroupCreateSchema } from '../schema/codeSchema';
import type { CodeGroupCreateFormValues } from '../schema/codeSchema';

interface CodeGroupCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: CodeGroupCreateFormValues) => void;
  isLoading?: boolean;
  errorMessage?: string;
  /** #438: groupCode 필드 인라인 에러 (예: 중복 409 시 같은 메시지 인라인 노출). */
  groupCodeFieldError?: string;
  /** #429: EXEC 샌드박스 컨텍스트 — true 이면 systemManaged 체크박스 숨김 (false 고정) */
  isExecSandbox?: boolean;
}

export function CodeGroupCreateDialog({
  open,
  onClose,
  onSubmit,
  isLoading = false,
  errorMessage,
  groupCodeFieldError,
  isExecSandbox = false,
}: CodeGroupCreateDialogProps) {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CodeGroupCreateFormValues>({
    resolver: zodResolver(codeGroupCreateSchema),
    defaultValues: {
      systemManaged: false,
    },
  });

  useEffect(() => {
    if (!open) {
      reset({ systemManaged: false });
    }
  }, [open, reset]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  const handleFormSubmit = handleSubmit((values) => {
    onSubmit(values);
  });

  const fieldStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-1)',
    marginBottom: 'var(--spacing-3)',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 'var(--font-size-xs)',
    fontWeight: 600,
    color: 'var(--color-text-secondary)',
  };

  const inputStyle: React.CSSProperties = {
    padding: 'var(--spacing-2) var(--spacing-3)',
    background: 'var(--color-surface-elevated)',
    border: '1px solid var(--color-surface-border)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-primary)',
    fontFamily: 'var(--font-family-sans)',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  };

  const errorStyle: React.CSSProperties = {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-danger)',
    marginTop: 'var(--spacing-1)',
  };

  const hintStyle: React.CSSProperties = {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-muted)',
    marginTop: 'var(--spacing-1)',
  };

  return (
    <FormDialog
      open={open}
      title={t('commonCode.createGroupDialog.title')}
      onClose={onClose}
      onSubmit={() => void handleFormSubmit()}
      submitLabel={isLoading ? t('commonCode.createGroupDialog.submitting') : t('commonCode.createGroupDialog.submit')}
      cancelLabel={t('commonCode.createGroupDialog.cancel')}
    >
      <div>
        {/* 에러 배너 */}
        {errorMessage && (
          <div
            role="alert"
            style={{
              marginBottom: 'var(--spacing-3)',
              padding: 'var(--spacing-3)',
              background: 'color-mix(in srgb, var(--color-danger) 12%, var(--color-surface-muted))',
              border: '1px solid var(--color-danger)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-danger)',
            }}
          >
            {errorMessage}
          </div>
        )}

        {/* 그룹 코드 */}
        <div style={fieldStyle}>
          <label htmlFor="create-group-code" style={labelStyle}>
            {t('commonCode.createGroupDialog.groupCode.label')}
            <span
              title={t('commonCode.createGroupDialog.groupCode.tooltip')}
              style={{ marginLeft: 'var(--spacing-1)', cursor: 'help', color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)' }}
              aria-label={t('commonCode.createGroupDialog.groupCode.tooltip')}
            >
              ?
            </span>
          </label>
          <input
            id="create-group-code"
            type="text"
            placeholder={t('commonCode.createGroupDialog.groupCode.placeholder')}
            {...register('groupCode')}
            aria-invalid={!!errors.groupCode || !!groupCodeFieldError}
            aria-describedby={errors.groupCode || groupCodeFieldError ? 'create-groupCode-error' : 'create-groupCode-hint'}
            style={{
              ...inputStyle,
              fontFamily: 'var(--font-family-mono)',
              borderColor: errors.groupCode || groupCodeFieldError ? 'var(--color-danger)' : 'var(--color-surface-border)',
            }}
          />
          {errors.groupCode ? (
            <span id="create-groupCode-error" style={errorStyle} role="alert">
              {errors.groupCode.message}
            </span>
          ) : groupCodeFieldError ? (
            <span id="create-groupCode-error" style={errorStyle} role="alert">
              {groupCodeFieldError}
            </span>
          ) : (
            <span id="create-groupCode-hint" style={hintStyle}>
              {t('commonCode.createGroupDialog.groupCode.hint')}
            </span>
          )}
        </div>

        {/* 그룹 이름 */}
        <div style={fieldStyle}>
          <label htmlFor="create-group-name" style={labelStyle}>
            {t('commonCode.createGroupDialog.groupName.label')}
            <span
              title={t('commonCode.createGroupDialog.groupName.tooltip')}
              style={{ marginLeft: 'var(--spacing-1)', cursor: 'help', color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)' }}
              aria-label={t('commonCode.createGroupDialog.groupName.tooltip')}
            >
              ?
            </span>
          </label>
          <input
            id="create-group-name"
            type="text"
            placeholder={t('commonCode.createGroupDialog.groupName.placeholder')}
            {...register('groupName')}
            aria-invalid={!!errors.groupName}
            aria-describedby={errors.groupName ? 'create-groupName-error' : undefined}
            style={{
              ...inputStyle,
              borderColor: errors.groupName ? 'var(--color-danger)' : 'var(--color-surface-border)',
            }}
          />
          {errors.groupName && (
            <span id="create-groupName-error" style={errorStyle} role="alert">
              {errors.groupName.message}
            </span>
          )}
        </div>

        {/* 설명 */}
        <div style={fieldStyle}>
          <label htmlFor="create-group-description" style={labelStyle}>
            {t('commonCode.createGroupDialog.description.label')}
          </label>
          <textarea
            id="create-group-description"
            placeholder={t('commonCode.createGroupDialog.description.placeholder')}
            rows={2}
            {...register('description')}
            style={{
              ...inputStyle,
              resize: 'vertical',
              minHeight: 60,
            }}
          />
        </div>

        {/* 시스템 관리 — #429: EXEC 샌드박스에서는 숨김 (false 고정, systemManaged=true 생성 차단) */}
        {!isExecSandbox && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--spacing-1)',
              marginBottom: 'var(--spacing-3)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
              <input
                id="create-group-systemManaged"
                type="checkbox"
                {...register('systemManaged')}
                style={{ width: 16, height: 16, accentColor: 'var(--color-brand-primary)', flexShrink: 0 }}
              />
              <label htmlFor="create-group-systemManaged" style={{ ...labelStyle, fontWeight: 400, cursor: 'pointer' }}>
                {t('commonCode.createGroupDialog.systemManaged.label')}
              </label>
            </div>
            <span style={hintStyle}>{t('commonCode.createGroupDialog.systemManaged.hint')}</span>
          </div>
        )}
      </div>
    </FormDialog>
  );
}
